import React, { useState, useEffect } from 'react';
import campaignService from '../../../services/campaignService';
import Icon from '../../../components/AppIcon';

/**
 * User Selection Modal Component
 * Allows filtering and selecting users for campaign recipients
 */
const UserSelectionModal = ({ filters, selectedUsers, onClose, onConfirm }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set(selectedUsers.map(u => u.id)));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    loadEligibleUsers();
  }, [filters]);

  const loadEligibleUsers = async () => {
    setLoading(true);
    try {
      const result = await campaignService.getEligibleRecipients(filters);
      if (result.success) {
        setUsers(result.data);
      } else {
        console.error('Error loading eligible users:', result.error);
      }
    } catch (error) {
      console.error('Error loading eligible users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = (userId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleConfirm = () => {
    const selected = users.filter(u => selectedIds.has(u.id));
    onConfirm(selected);
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Select Recipients</h2>
            <p className="text-sm text-gray-600 mt-1">
              Users who have opted in to marketing emails
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Search and Stats */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-primary">{selectedIds.size}</span> of{' '}
              <span className="font-semibold">{filteredUsers.length}</span> selected
            </div>
          </div>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-gray-600 mt-4">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No eligible users found</p>
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your filters or make sure users have opted in to marketing emails
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 pb-3 border-b">
                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="font-medium text-gray-900">
                    Select All ({filteredUsers.length} users)
                  </span>
                </label>
              </div>

              {/* User Items */}
              <div className="space-y-2">
                {filteredUsers.map(user => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => handleToggleUser(user.id)}
                      className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">{user.full_name}</p>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          user.role === 'va' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'employer' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      <p className="text-xs text-gray-400">
                        Joined {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">{selectedIds.size}</span> recipient{selectedIds.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Icon name="Check" size={18} />
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSelectionModal;
